import exp from "constants";
import { format } from "date-fns";

export const getPageRange = (current: number, total: number) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  if (current <= 3) {
    return [1, 2, 3, 4, 5, "...", total];
  }

  if (current >= total - 2) {
    return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
  }

  return [1, "...", current - 1, current, current + 1, "...", total];
};

export const getInitials = (fullname: string | undefined): string => {
  if (!fullname) return "";
  return fullname
    .split(/[\s\-–—_/]+/)
    .map((part) => part.replace(/[^a-zA-Z0-9]/g, ""))
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export const convertDateToUTC = (dateStr: string | null | undefined) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toISOString().split("T")[0];
};
export const convertDateToLocal = (dateStr: string | null | undefined) => {
  if (!dateStr) return null;

  const utcDate = dateStr.includes("Z") ? dateStr : `${dateStr}T00:00:00.000Z`;

  const date = new Date(dateStr);

  

  return date.toLocaleString();
};
export const convertTimeToUTC = (localTime: string | null) => {
  if (!localTime) return null;

  try {
    const today = new Date();

    const timeRegex = /^(\d{2}):(\d{2}):(\d{2})\.(\d{3})([-+])(\d{2}):(\d{2})$/;
    const match = localTime.match(timeRegex);

    if (!match) {
      throw new Error("Invalid time format");
    }

    const [
      ,
      hours,
      minutes,
      seconds,
      milliseconds,
      sign,
      offsetHours,
      offsetMinutes,
    ] = match;

    const localDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      parseInt(hours),
      parseInt(minutes),
      parseInt(seconds),
      parseInt(milliseconds)
    );

    const offsetInMinutes =
      parseInt(offsetHours) * 60 + parseInt(offsetMinutes);

    const adjustedMinutes = sign === "+" ? -offsetInMinutes : offsetInMinutes;

    localDate.setMinutes(localDate.getMinutes() + adjustedMinutes);

    // Format UTC time
    const utcHours = localDate.getUTCHours().toString().padStart(2, "0");
    const utcMinutes = localDate.getUTCMinutes().toString().padStart(2, "0");
    const utcSeconds = localDate.getUTCSeconds().toString().padStart(2, "0");
    const utcMilliseconds = localDate
      .getUTCMilliseconds()
      .toString()
      .padStart(3, "0");

    return `${utcHours}:${utcMinutes}:${utcSeconds}.${utcMilliseconds}Z`;
  } catch (error) {
    console.error("Error converting time to UTC:", error);
    return null;
  }
};

export const convertUTCToLocalTime = (utcTime: string | null) => {
  if (!utcTime) return null;

  try {
    const today = new Date();
    const offsetMinutes = today.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
    const offsetMins = Math.abs(offsetMinutes % 60);

    const [timePart, millisPart = "000000"] = utcTime.split(".");
    const [hours, minutes, seconds] = timePart.split(":");

    const milliseconds = millisPart.slice(0, 3);

    const utcDate = new Date();
    utcDate.setUTCHours(
      parseInt(hours),
      parseInt(minutes),
      parseInt(seconds),
      parseInt(milliseconds)
    );

    const offsetSign = offsetMinutes > 0 ? "-" : "+";
    const offsetString = `${offsetSign}${offsetHours
      .toString()
      .padStart(2, "0")}:${offsetMins.toString().padStart(2, "0")}`;

    const localHours = utcDate.getHours().toString().padStart(2, "0");
    const localMinutes = utcDate.getMinutes().toString().padStart(2, "0");
    const localSeconds = utcDate.getSeconds().toString().padStart(2, "0");
    const localMilliseconds = utcDate
      .getMilliseconds()
      .toString()
      .padStart(3, "0");

    return `${localHours}:${localMinutes}:${localSeconds}.${localMilliseconds}${offsetString}`;
  } catch (error) {
    console.error("Error converting UTC to local time:", error);
    return null;
  }
};

export const calculateAverageResponseTime = (calls?: any[]) => {
  if (!calls || calls.length === 0) return "0";

  const validCalls = calls.filter((call) => call.start_time && call.end_time);

  if (validCalls.length === 0) return "0";

  const totalResponseTime = validCalls.reduce((acc, call) => {
    const start = new Date(call.start_time);
    const end = new Date(call.end_time);
    return acc + (end.getTime() - start.getTime());
  }, 0);

  const averageHours = (
    totalResponseTime /
    validCalls.length /
    (1000 * 60 * 60)
  ).toFixed(1);

  return averageHours;
};
export const calculateAverageCallDuration = (calls: any[]) => {
  if (!calls || calls.length === 0) return "0m";

  const completedCalls = calls.filter((call) => call.status === "COMPLETED");
  if (completedCalls.length === 0) return "0m";

  const totalDuration = completedCalls.reduce((acc, call) => {
    const start = new Date(call.start_time);
    const end = new Date(call.end_time);
    return acc + (end.getTime() - start.getTime());
  }, 0);

  const avgMinutes = Math.round(
    totalDuration / completedCalls.length / 1000 / 60
  );
  return `${avgMinutes}m`;
};
export function calculateCallDuration(startTime: string, endTime: string) {
  if (!startTime || !endTime) return "N/A";

  const start = new Date(startTime);
  const end = new Date(endTime);
  const durationMs = end.getTime() - start.getTime();

  const minutes = Math.floor(durationMs / 60000);
  const seconds = Math.floor((durationMs % 60000) / 1000);

  return `${minutes}m ${seconds}s`;
}

export const SALES_TEAM_STATUSES = {
  NEW: "NEW",
  FOLLOWUP: "FOLLOW_UP_NEEDED",
  INTERESTED: "INTERESTED",
  CONVERTED: "CONVERTED",
  NOANSWER: "CONTACTED_NO_ANSWER",
  UNREACHABLE: "UNREACHABLE",
  NOTINTERESTED: "NOT_INTERESTED",
  LOST: "LOST",
  DISQUALIFIED: "DISQUALIFIED",
} as const;

export const SUPPORT_TEAM_STATUSES = {
  NEW: "NEW",
  INPROGRESS: "IN_PROGRESS",
  WAITING: "WAITING_FOR_CUSTOMER",
  ESCALATED: "ESCALATED",
  RESOLVED: "RESOLVED",
  CLOSED: "CLOSED",
  REOPENED: "REOPENED",
  ONHOLD: "ON_HOLD",
  CANCELLED: "CANCELLED",
} as const;

export const SALES_STATUS_DISPLAY = {
  NEW: "New",
  FOLLOWUP: "Follow Up Needed",
  INTERESTED: "Interested",
  CONVERTED: "Converted",
  NOANSWER: "No Answer",
  UNREACHABLE: "Unreachable",
  NOTINTERESTED: "Not Interested",
  LOST: "Lost",
  DISQUALIFIED: "Disqualified",
} as const;

export const SUPPORT_STATUS_DISPLAY = {
  NEW: "New",
  INPROGRESS: "In progress",
  WAITING: "Waiting for Customer",
  ESCALATED: "Escalated",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
  REOPENED: "Reopened",
  ONHOLD: "On Hold",
  CANCELLED: "Cancelled",
} as const;

export const getStatusDisplay = (
  status: string,
  type: "sales" | "support"
): string => {
  if (type === "sales") {
    const key = Object.keys(SALES_TEAM_STATUSES).find(
      (k) =>
        SALES_TEAM_STATUSES[k as keyof typeof SALES_TEAM_STATUSES] === status
    );
    return key
      ? SALES_STATUS_DISPLAY[key as keyof typeof SALES_STATUS_DISPLAY]
      : status;
  } else {
    const key = Object.keys(SUPPORT_TEAM_STATUSES).find(
      (k) =>
        SUPPORT_TEAM_STATUSES[k as keyof typeof SUPPORT_TEAM_STATUSES] ===
        status
    );
    return key
      ? SUPPORT_STATUS_DISPLAY[key as keyof typeof SUPPORT_STATUS_DISPLAY]
      : status;
  }
};

export const convertUTCToLocal = (utcDate: string): string => {
  const date = new Date(utcDate);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const convertToUTCISO = (
  dateStr: string | null | undefined
): string | null => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return date.toISOString(); // Returns format like 2024-03-20T10:30:00Z
  } catch (error) {
    console.error("Error converting date to UTC ISO:", error);
    return null;
  }
};

export const TEXTAREA_MIN_HEIGHT = "min-h-[120px]";

export const PROMPT_TEXT_EDITOR_CLASS = "min-h-[200px] resize-none";
/** Only scroll internally when focused so page wheel isn't trapped on hover. */
export const PROMPT_TEXT_EDITOR_INNER_CLASS =
  "h-[200px] overflow-y-hidden focus:overflow-y-auto";


export const DEFAULT_SORT_COLUMN_CREATED_AT = "created_at" as const;

export const DEFAULT_SORT_ORDER_DESC = "desc" as const;

export const DEFAULT_TABLE_SORT_BY_CREATED_AT = {
  sortBy: DEFAULT_SORT_COLUMN_CREATED_AT,
  sortOrder: DEFAULT_SORT_ORDER_DESC,
} as const;


/** The API rejects any `limit` above this, so page through rather than raise it. */
export const MAX_API_PAGE_LIMIT = 50;

export const DEFAULT_TABLE_PAGE_LIMIT = 30;

const LIST_PAGE_STATE_PREFIX = "list_page_state_";
const PAGE_STATE_STORAGE_PREFIX = "pageState:";

export const LIST_PAGE_STORAGE_KEYS = {
  SALES_TEAM_LEADS: `${LIST_PAGE_STATE_PREFIX}sales_team_leads`,
  SUPPORT_TEAM_CUSTOMERS: `${LIST_PAGE_STATE_PREFIX}support_team_customers`,
} as const;

const LIST_PAGE_KEYS_SET = new Set<string>(Object.values(LIST_PAGE_STORAGE_KEYS));

export function pageState<T extends Record<string, unknown>>(
  key: string,
  defaults: T,
  stateToPersist?: T
): T {
  if (typeof window === "undefined") return stateToPersist ?? defaults;

  const storageKey = LIST_PAGE_KEYS_SET.has(key) ? key : `${PAGE_STATE_STORAGE_PREFIX}${key}`;

  if (stateToPersist !== undefined) {
    try {
      const toSave: Record<string, unknown> = { ...stateToPersist };
      const dr = toSave.dateRange;
      if (dr && typeof dr === "object" && dr !== null && ("from" in dr || "to" in dr)) {
        const d = dr as { from?: Date; to?: unknown };
        toSave.dateRange = {
          from: d.from instanceof Date ? d.from.toISOString() : d.from,
          to: d.to instanceof Date ? (d.to as Date).toISOString() : d.to,
        };
      }
      localStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch (e) {
      console.warn("[pageState] Failed to persist:", e);
    }
    return stateToPersist;
  }

  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, unknown> = { ...defaults };
    for (const k of Object.keys(parsed)) {
      const v = parsed[k];
      if (v === null || v === undefined) continue;
      if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
        const d = new Date(v);
        if (!Number.isNaN(d.getTime())) {
          out[k] = d;
          continue;
        }
      }
      if (typeof v === "object" && v !== null && !Array.isArray(v)) {
        const sub = v as Record<string, unknown>;
        if ("from" in sub || "to" in sub) {
          out[k] = {
            from: sub.from && typeof sub.from === "string" ? new Date(sub.from) : undefined,
            to: sub.to && typeof sub.to === "string" ? new Date(sub.to) : undefined,
          };
          continue;
        }
      }
      out[k] = v;
    }
    return out as T;
  } catch {
    return defaults;
  }
}

export const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export function formatTime(dateString: string) {
  return new Date(dateString).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export interface FormattedDateTime {
  date: string;
  time: string;
  full: string;
}

export const formatDateTime = (
  dateString: string | Date
): FormattedDateTime => {
  const dateObj =
    typeof dateString === "string" ? new Date(dateString) : dateString;

  const date = format(dateObj, "MMM d, yyyy");
  const time = format(dateObj, "hh:mm a");
  const full = format(dateObj, "MMM d, yyyy 'at' hh:mm a");

  return { date, time, full };
};

export const formatFullDateTime = (dateString: string | Date): string => {
  const dateObj =
    typeof dateString === "string" ? new Date(dateString) : dateString;
  return format(dateObj, "MMM d, yyyy 'at' hh:mm a");
};

export const formatDateTimeInline = (dateString: string | Date): string => {
  const dateObj =
    typeof dateString === "string" ? new Date(dateString) : dateString;
  return format(dateObj, "MMM d, yyyy 'at' hh:mm a");
};

export const formatDateTimeCompact = (dateString: string | Date): string => {
  const dateObj =
    typeof dateString === "string" ? new Date(dateString) : dateString;
  return format(dateObj, "MMM d, yyyy hh:mm a");
};

export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds} sec${seconds !== 1 ? "s" : ""}`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    if (remainingSeconds === 0) {
      return `${minutes} min${minutes !== 1 ? "s" : ""}`;
    }
    return `${minutes} min${minutes !== 1 ? "s" : ""
      } ${remainingSeconds} sec${remainingSeconds !== 1 ? "s" : ""}`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  }
  return `${hours} hour${hours !== 1 ? "s" : ""} ${remainingMinutes} min${remainingMinutes !== 1 ? "s" : ""
    }`;
};

export const getYouTubeEmbedUrl = (url: string): string => {
  const videoId =
    url.split("v=")[1]?.split("&")[0] ||
    url.split("youtu.be/")[1]?.split("?")[0] ||
    url.split("youtube.com/embed/")[1]?.split("?")[0];

  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }
  return url;
};

export const formatTimeQuizResults = (seconds: number) => {
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? "s" : ""}`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    if (remainingSeconds === 0) {
      return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
    }
    return `${minutes} minute${minutes !== 1 ? "s" : ""
      } ${remainingSeconds} second${remainingSeconds !== 1 ? "s" : ""}`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  }
  return `${hours} hour${hours !== 1 ? "s" : ""} ${remainingMinutes} minute${remainingMinutes !== 1 ? "s" : ""
    }`;
};

export const formatDurationQuizCard = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes === 0) {
    return `${remainingSeconds} seconds`;
  } else if (remainingSeconds === 0) {
    return `${minutes} minute${minutes > 1 ? "s" : ""}`;
  } else {
    return `${minutes} minute${minutes > 1 ? "s" : ""
      } ${remainingSeconds} seconds`;
  }
};

export const formatDateTimeQuizCard = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString();
};

export const formatTimeQuizQuestions = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

export const formatDurationToHours = (seconds: number): string => {
  const hours = Math.round((seconds / 3600) * 10) / 10;
  return hours.toString();
};






import { VoiceParamSchema } from "@/types/vapi-voices";

export const transformArrayToObject = (
  arr: string[],
  separator: string = ":"
): Record<string, string> => {
  const result: Record<string, string> = {};
  if (!Array.isArray(arr)) return result;

  arr.forEach((item) => {
    if (typeof item === "string" && item.includes(separator)) {
      const [key, value] = item.split(separator);
      if (key && value) {
        result[key.trim()] = value.trim();
      }
    }
  });
  return result;
};

export const transformObjectToArray = (
  obj: Record<string, string>,
  separator: string = ":"
): string[] => {
  if (!obj || typeof obj !== "object") return [];

  return Object.entries(obj)
    .filter(([_, value]) => value && value !== "none")
    .map(([key, value]) => `${key}${separator}${value}`);
};

export const getDefaultValueForSchema = (schema: VoiceParamSchema): any => {
  // Preserve explicit null — means "unset / provider decides", not false/0
  if (Object.prototype.hasOwnProperty.call(schema, "default")) {
    return schema.default;
  }

  switch (schema.type) {
    case "bool":
      return false;
    case "str":
      return "";
    case "int":
    case "float":
      return schema.min ?? 0;
    case "union":
    case "enum":
      if (schema.optional !== false) return null;
      return schema.values?.[0] || "";
    case "object":
      return {};
    case "array":
      return [];
    default:
      return null;
  }
};

export const initializeVoiceParamsWithDefaults = (
  voiceParams: Record<string, any>,
  paramsSchema: Record<string, VoiceParamSchema>
): Record<string, any> => {
  const initialized = { ...voiceParams };

  Object.entries(paramsSchema).forEach(([paramName, schema]) => {
    if (paramName === "example_schema") {
      return;
    }

    if (initialized[paramName] === undefined) {
      const defaultValue = getDefaultValueForSchema(schema);
      // Skip null/undefined defaults — leave unset so the provider decides
      if (defaultValue !== null && defaultValue !== undefined && defaultValue !== "") {
        initialized[paramName] = defaultValue;
      } else if (defaultValue === false || typeof defaultValue === "number") {
        initialized[paramName] = defaultValue;
      }
    }
  });

  return initialized;
};

export const parseVoiceParams = (
  apiParams: any,
  paramsSchema: Record<string, VoiceParamSchema>
): Record<string, any> => {
  if (!apiParams) return {};

  let params: any;
  try {
    params = Array.isArray(apiParams) ? apiParams[0] || {} : apiParams;
    if (typeof params !== "object" || params === null) {
      return {};
    }
  } catch (error) {
    console.error("Error parsing voice params:", error);
    return {};
  }

  const result: Record<string, any> = {};

  try {
    Object.entries(params).forEach(([paramName, value]: [string, any]) => {
      const schema = paramsSchema[paramName];

      if (!schema) {
        result[paramName] = value;
        return;
      }

      if (schema.transform === "array_of_pairs" && Array.isArray(value)) {
        result[paramName] = transformArrayToObject(
          value,
          schema.separator || ":"
        );
      } else if (
        schema.type === "object" &&
        schema.properties &&
        value &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        const nestedResult: Record<string, any> = {};
        Object.entries(value).forEach(
          ([nestedKey, nestedValue]: [string, any]) => {
            const nestedSchema = schema.properties?.[nestedKey];
            if (
              nestedSchema?.transform === "array_of_pairs" &&
              Array.isArray(nestedValue)
            ) {
              nestedResult[nestedKey] = transformArrayToObject(
                nestedValue,
                nestedSchema.separator || ":"
              );
            } else {
              nestedResult[nestedKey] = nestedValue;
            }
          }
        );
        result[paramName] = nestedResult;
      } else {
        result[paramName] = value;
      }
    });
  } catch (error) {
    console.error("Error transforming voice params:", error);
  }

  return result;
};

export const serializeVoiceParams = (
  params: Record<string, any>,
  paramsSchema: Record<string, VoiceParamSchema>
): Record<string, any> => {
  const result: Record<string, any> = {};

  if (!params || typeof params !== "object") {
    return result;
  }

  try {
    Object.entries(params).forEach(([paramName, value]: [string, any]) => {
      const schema = paramsSchema[paramName];

      if (!schema) {
        if (value !== null && value !== undefined && value !== "") {
          result[paramName] = value;
        }
        return;
      }

      if (
        schema.transform === "array_of_pairs" &&
        value &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        const arrayValue = transformObjectToArray(
          value,
          schema.separator || ":"
        );
        if (arrayValue.length > 0) {
          result[paramName] = arrayValue;
        }
      } else if (
        schema.type === "object" &&
        schema.properties &&
        value &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        const nestedResult: Record<string, any> = {};
        Object.entries(value).forEach(
          ([nestedKey, nestedValue]: [string, any]) => {
            const nestedSchema = schema.properties?.[nestedKey];

            if (
              nestedSchema?.transform === "array_of_pairs" &&
              nestedValue &&
              typeof nestedValue === "object" &&
              !Array.isArray(nestedValue)
            ) {
              const arrayValue = transformObjectToArray(
                nestedValue as Record<string, string>,
                nestedSchema.separator || ":"
              );
              if (arrayValue.length > 0) {
                nestedResult[nestedKey] = arrayValue;
              }
            } else if (
              nestedValue !== null &&
              nestedValue !== undefined &&
              nestedValue !== ""
            ) {
              nestedResult[nestedKey] = nestedValue;
            }
          }
        );

        if (Object.keys(nestedResult).length > 0) {
          result[paramName] = nestedResult;
        }
      } else if (value !== null && value !== undefined && value !== "") {
        result[paramName] = value;
      }
    });
  } catch (error) {
    console.error("Error serializing voice params:", error);
  }

  return result;
};

export const PAGE_SIZES_MM: Record<string, { w: number; h: number }> = {
  A4: { w: 210, h: 297 },
  A3: { w: 297, h: 420 },
  A5: { w: 148, h: 210 },
  Letter: { w: 216, h: 279 },
};
export const AI_MODEL_OPTIONS = [
  { label: "GPT-4o", value: "GPT_4O" },
  { label: "GPT-4o mini", value: "GPT_4O_MINI" },
  { label: "GPT-4o mini (2024-07-18)", value: "GPT_4O_MINI_2024_07_18" },
  { label: "DALL-E 3", value: "DALLE_3" },
] as const;

export const TEXT_AI_MODELS = ["GPT_4O", "GPT_4O_MINI", "GPT_4O_MINI_2024_07_18"];
export const IMAGE_AI_MODELS = ["DALLE_3"];

export const isElementSaved = (elementId: any) => {
  
  if (typeof elementId === 'string' && elementId.includes('-')) {
    return true;
  }
  if (typeof elementId === 'number' && elementId < 1000000000000) {
    return true;
  }
  return false;
};

// Ticket Badge Colors
export const TICKET_PRIORITY_COLORS: Record<string, string> = {
  high: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  medium: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
  low: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  urgent: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  normal: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
};

export const TICKET_STATUS_COLORS: Record<string, string> = {
  new: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
  solved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  open: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  pending: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  closed: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  resolved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
};

export const getTicketPriorityColor = (priority: string): string => {
  if (!priority) return TICKET_PRIORITY_COLORS.low;
  return TICKET_PRIORITY_COLORS[priority.toLowerCase()] || TICKET_PRIORITY_COLORS.low;
};

export const getTicketStatusColor = (status: string): string => {
  if (!status) return TICKET_STATUS_COLORS.pending;
  return TICKET_STATUS_COLORS[status.toLowerCase()] || TICKET_STATUS_COLORS.pending;
};

export const formatTokenCount = (tokens: unknown): string => {
  if (tokens === null || tokens === undefined || tokens === "") return "N/A";

  const numTokens = typeof tokens === "string" ? parseFloat(tokens) : Number(tokens);

  if (isNaN(numTokens)) return "N/A";
  if (numTokens < 1000) return numTokens.toString();
  if (numTokens < 1_000_000) return `${(numTokens / 1000).toFixed(1)}K`;
  if (numTokens < 1_000_000_000) return `${(numTokens / 1_000_000).toFixed(1)}M`;
  return `${(numTokens / 1_000_000_000).toFixed(1)}B`;
};

export const ARTICULATE_BRITISH_FEMALE_VOICE = {
  voice_id: "5TRppDPuxBF23owe37hG",
  name: "Articulate British Female",
};

export const SEARCH_DEBOUNCE_WAIT = 700;
export const MIN_SEARCH_CHARS = 3;
